// frontend/src/components/chat/MessageList.js
// Lista de mensajes con scroll infinito

import React, { useRef, useEffect, useState } from 'react';
import MessageItem from './MessageItem';
import './MessageList.css';

const MessageList = ({ messages, loading, currentUserId, onLoadMore, hasMore, onReply }) => {
    const containerRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    // Scroll al final cuando llegan nuevos mensajes
    useEffect(() => {
        if (isAtBottom && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [messages, isAtBottom]);

    // Detectar posicion de scroll
    const handleScroll = () => {
        if (!containerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const atBottom = scrollHeight - scrollTop - clientHeight < 50;
        setIsAtBottom(atBottom);

        // Cargar mas al llegar arriba
        if (scrollTop < 100 && hasMore && !loading) {
            onLoadMore?.();
        }
    };

    // Agrupar mensajes por fecha
    const groupMessagesByDate = (msgs) => {
        const groups = [];
        let currentDate = null;

        msgs.forEach((msg, index) => {
            const msgDate = new Date(msg.created_at).toLocaleDateString('es-EC', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
            });

            if (msgDate !== currentDate) {
                currentDate = msgDate;
                groups.push({ type: 'date', date: msgDate, key: `date-${index}` });
            }
            groups.push({ type: 'message', data: msg, key: `msg-${msg.id}` });
        });

        return groups;
    };

    const groupedMessages = groupMessagesByDate(messages);

    return (
        <div
            className="urbanq-message-list"
            ref={containerRef}
            onScroll={handleScroll}
        >
            {loading && messages.length === 0 ? (
                <div className="urbanq-message-loading">
                    <div className="urbanq-loading-spinner"></div>
                    <span>Cargando mensajes...</span>
                </div>
            ) : messages.length === 0 ? (
                <div className="urbanq-message-empty">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="urbanq-message-empty-icon">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                    </svg>
                    <p>No hay mensajes aun</p>
                    <span>Se el primero en enviar un mensaje!</span>
                </div>
            ) : (
                <>
                    {loading && hasMore && (
                        <div className="urbanq-message-loading-more">
                            <div className="urbanq-loading-spinner small"></div>
                        </div>
                    )}

                    {groupedMessages.map((item, index) => {
                        if (item.type === 'date') {
                            return (
                                <div key={item.key} className="urbanq-message-date-separator">
                                    <span>{item.date}</span>
                                </div>
                            );
                        }

                        const msg = item.data;
                        const isOwn = msg.sender_id === currentUserId;
                        const prevItem = groupedMessages[index - 1];
                        const nextItem = groupedMessages[index + 1];
                        const prevMsg = prevItem?.type === 'message' ? prevItem.data : null;
                        const nextMsg = nextItem?.type === 'message' ? nextItem.data : null;
                        const showAvatar = !isOwn && (!nextMsg || nextMsg.sender_id !== msg.sender_id);
                        const isGroupStart = !prevMsg || prevMsg.sender_id !== msg.sender_id;
                        const isGroupEnd = !nextMsg || nextMsg.sender_id !== msg.sender_id;

                        return (
                            <MessageItem
                                key={item.key}
                                message={msg}
                                isOwn={isOwn}
                                showAvatar={showAvatar}
                                isGroupStart={isGroupStart}
                                isGroupEnd={isGroupEnd}
                                onReply={onReply}
                            />
                        );
                    })}
                </>
            )}

            {/* Boton para ir al final */}
            {!isAtBottom && (
                <button
                    className="urbanq-scroll-bottom"
                    onClick={() => {
                        if (containerRef.current) {
                            containerRef.current.scrollTop = containerRef.current.scrollHeight;
                        }
                    }}
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                </button>
            )}
        </div>
    );
};

export default MessageList;
